<template>
	<div id="overview" class="dashboard">
		<Bvn @refresh-data="getOverviewDetails" />

		<!-- ═══════════════════════════════════════════════════════════════════ -->
		<!-- HERO: Portfolio Overview -->
		<!-- ═══════════════════════════════════════════════════════════════════ -->
		<section class="hero-section">
			<div class="hero-card">
				<div class="hero-content">
					<!-- Left: Portfolio Value -->
					<div class="portfolio-main">
						<div class="portfolio-header">
							<span class="portfolio-label">Portfolio Value</span>
							<div class="currency-toggle">
								<button 
									v-for="wallet in walletBalance?.slice(0, 3)" 
									:key="wallet.id"
									@click="selectCurrency(wallet)"
									:class="['currency-btn', { active: selectedCurrency === wallet.currency }]"
								>
									<CurrencyFlag :currency="wallet.currency" class="tw-w-4 tw-h-4" />
									{{ getCurrencySymbol(wallet.currency) }}
								</button>
							</div>
						</div>
						
						<div class="portfolio-value-container">
							<h1 class="portfolio-value">
								{{ formatNumber(positions.length) }} <span class="shares-label">{{ positions.length === 1 ? "position" : "positions" }}</span>
							</h1>
							<p v-if="positionsBreakdown" class="positions-breakdown">{{ positionsBreakdown }}</p>
							<div class="cash-value-display">
								<span class="cash-value-label">Cash Value:</span>
								<span class="cash-value-amount">{{ formatAmountToDollar(portfolioValue, 2, selectedCurrency || 'USD') }}</span>
							</div>
						</div>

						<!-- Monthly Stats -->
						<div class="monthly-stats">
							<div class="monthly-stat">
								<span class="monthly-stat-label">Monthly Growth</span>
								<span class="monthly-stat-value" :class="monthlyGrowth >= 0 ? 'positive' : 'negative'">
									{{ monthlyGrowth >= 0 ? '+' : '' }}{{ monthlyGrowthPercent }}%
								</span>
							</div>
							<div class="monthly-stat" v-if="monthlyPayout > 0">
								<span class="monthly-stat-label">Payouts This Month</span>
								<span class="monthly-stat-value payout">
									{{ formatAmountToDollar(monthlyPayout, 2, selectedCurrency || 'USD') }}
								</span>
							</div>
						</div>
					</div>

					<!-- Right: Quick Stats -->
					<div class="quick-stats">
						<div class="stat-item">
							<span class="stat-label">Available Cash</span>
							<span class="stat-value">{{ formatAmountToDollar(selectedWallet?.balance || 0, 2, selectedCurrency) }}</span>
							<button @click="$router.push({ name: 'Wallet' })" class="fund-btn">
								<svg class="tw-w-4 tw-h-4" viewBox="0 0 16 16" fill="none">
									<path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
								</svg>
								Add Funds
							</button>
						</div>
						<div class="stat-item">
							<span class="stat-label">Invested</span>
							<span class="stat-value">{{ formatAmountToDollar(investedValue, 2, selectedCurrency) }}</span>
							<router-link :to="{ name: 'Wallet', query: { currency: selectedCurrency, tab: 'investments' } }" class="stat-link">
								{{ tokens?.length || 0 }} positions →
							</router-link>
						</div>
						<div class="stat-item">
							<span class="stat-label">Pending Orders</span>
							<span class="stat-value">{{ pendingOrdersCount }}</span>
							<router-link :to="{ name: 'InvestOrderbook' }" class="stat-link">View orders →</router-link>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- ═══════════════════════════════════════════════════════════════════ -->
		<!-- MAIN CONTENT GRID -->
		<!-- ═══════════════════════════════════════════════════════════════════ -->
		<div class="main-grid">
			<!-- Left Column: Primary Content -->
			<div class="main-column">
				<!-- NOW RAISING: Featured Opportunities -->
				<section class="content-section" id="step1">
					<div class="section-header">
						<div class="section-title-group">
							<h2 class="section-title">Now Raising</h2>
							<span class="live-indicator">
								<span class="live-dot"></span>
								Live
							</span>
						</div>
						<router-link :to="{ name: 'Opportunities' }" class="section-link">
							View all opportunities →
						</router-link>
					</div>
					<div class="opportunities-grid">
						<RaisingTable :limit="3" />
					</div>
				</section>

				<!-- EXCHANGE: Trade Shares -->
				<section class="content-section" id="step2">
					<div class="section-header">
						<div class="section-title-group">
							<h2 class="section-title">Exchange</h2>
							<span class="badge-count">{{ exchangeTokensCount }} listed</span>
						</div>
						<router-link :to="{ name: 'Exchange' }" class="section-link">
							Open exchange →
						</router-link>
					</div>
					<RaisedCard />
				</section>

				<!-- ACTIVITY: Recent Transactions -->
				<section class="content-section" id="step3">
					<div class="section-header">
						<h2 class="section-title">Recent Activity</h2>
						<router-link :to="{ name: 'Wallet' }" class="section-link">
							View history →
						</router-link>
					</div>
					<div class="activity-card">
						<ActivityTimeline />
					</div>
				</section>
			</div>

			<!-- Right Column: Secondary Content -->
			<div class="side-column">
				<!-- YOUR HOLDINGS -->
				<section class="side-card holdings-card">
					<div class="side-header">
						<h3 class="side-title">Your Holdings</h3>
						<router-link :to="{ name: 'Wallet', query: { tab: 'investments' } }" class="side-link">See all</router-link>
					</div>
					<div v-if="tokens?.length > 0" class="holdings-list">
						<div 
							v-for="(token, index) in tokens?.slice(0, 5)" 
							:key="index" 
							class="holding-item"
							@click="goToToken(token)"
						>
							<div class="holding-info">
								<img :src="token.image" :alt="token.name" class="holding-logo" />
								<div>
									<p class="holding-name">{{ token.name }}</p>
									<p class="holding-symbol">{{ token.symbol }} · {{ holdingType(token) }}</p>
								</div>
							</div>
							<div class="holding-value">
								<p class="holding-amount">{{ formatAmountToDollar(token.usd_value || 0, 2, token.currency || 'USD') }}</p>
								<p class="holding-shares">{{ holdingQuantity(token) }}</p>
							</div>
						</div>
					</div>
					<div v-else class="empty-state">
						<div class="empty-icon">
							<svg viewBox="0 0 48 48" fill="none">
								<circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="2" stroke-dasharray="4 4"/>
								<path d="M24 16V32M16 24H32" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
							</svg>
						</div>
						<p class="empty-text">No investments yet</p>
						<router-link :to="{ name: 'Invest' }" class="empty-cta">
							Start Investing
						</router-link>
					</div>
				</section>


				<!-- STOCK OPTIONS -->

				<!-- PENDING ORDERS -->
				<section class="side-card">
					<div class="side-header">
						<h3 class="side-title">Pending Orders</h3>
						<router-link :to="{ name: 'InvestOrderbook' }" class="side-link">View all</router-link>
					</div>
					<div class="orders-card">
						<OrderBookTable compact />
					</div>
				</section>
			</div>
		</div>

		<!-- Modals -->
		<AboutInvestments :show="showModal" @close-modal="showModal = false" />
		
	</div>
</template>

<script>
import Btn from "@/components/general/BtnComponent.vue";
import SmallModal from "@/components/general/SmallModal.vue";
import Bvn from "@/components/kyc/Bvn";
import AboutInvestments from "@/components/overview/AboutInvestments";
import RaisedCard from "@/components/overview/RaisedCard";
import CurrencyFlag from "@/components/wallet/CurrencyFlag.vue";
// investor-app's table, not this app's 460-line one — the overview's Pending Orders card
// is meant to match investor-app, and the two components are entirely different.
import OrderBookTable from "@/components/tables/OrderBookTable";
import RaisingTable from "@/components/tables/RaisingTable";
import TransactionTable from "@/components/tables/TransactionTable";
import ActivityTimeline from "@/components/overview/ActivityTimeline";
import { useFundNotification } from "@/composables/notifyUserFunding";
import { formatAmountToDollar, getCurrencySymbol } from "@/utils/helpers.js";
import { holdingQuantity, listingView } from "@/utils/trade-view";
import responseHandler from "@/utils/responseHandler.js";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
	name: "Invest",

	components: {
						RaisingTable,
		TransactionTable,
		ActivityTimeline,
		OrderBookTable,
		RaisedCard,
						AboutInvestments,
		SmallModal,
		Btn,
		Bvn,
				CurrencyFlag
	},

	data() {
		return {
			showModal: false,
			loading: false,
			selectedCurrency: 'USD',
			selectedWallet: null,
			monthlyGrowth: 0,
			monthlyGrowthPercent: 0,
			monthlyPayout: 0,
		};
	},

	computed: {
		...mapState({
			tokens: (state) => state.TokenModule.userTokens,
			unlimitedUserTokens: (state) => state.TokenModule.unlimitedUserTokens,
			raisedTokens: (state) => state.TokenModule.raisedTokens,
		}),
		...mapGetters("organisationModule", ["walletBalance", "organisationDetails"]),

		// Portfolio Value = Sum of balance * price.buy (filtered by selected currency)
		portfolioValue() {
			const tokenList = this.unlimitedUserTokens || this.tokens;
			if (!tokenList?.length) return 0;
			return tokenList
				.filter(token => (token.currency || 'USD').toUpperCase() === (this.selectedCurrency || 'USD').toUpperCase())
				.reduce((sum, token) => {
					const balance = token.balance || 0;
					const price = token.price?.buy || token.price?.exchange || 0;
					return sum + (balance * price);
				}, 0);
		},

		// Cash Value = Wallet balance for selected currency
		cashValue() {
			return this.selectedWallet?.balance || 0;
		},

		// Total Value = Portfolio + Cash (for reference, but not displayed as main value)
		totalValue() {
			return this.portfolioValue + this.cashValue;
		},

		// Holdings in the selected currency that are actually held.
		positions() {
			const tokenList = this.unlimitedUserTokens || this.tokens;
			if (!tokenList?.length) return [];
			return tokenList
				.filter(token => (token.currency || 'USD').toUpperCase() === (this.selectedCurrency || 'USD').toUpperCase())
				.filter(token => Number(token.balance) > 0);
		},

		// "5 debt · 1 equity · 1 commodity", largest first.
		positionsBreakdown() {
			const counts = new Map();
			this.positions.forEach((token) => {
				const label = this.holdingType(token).toLowerCase();
				counts.set(label, (counts.get(label) || 0) + 1);
			});
			return [...counts.entries()]
				.sort((a, b) => b[1] - a[1])
				.map(([label, count]) => `${count} ${label}`)
				.join(" · ");
		},

		// Invested Value = balance * price.buy (filtered by selected currency)
		investedValue() {
			const tokenList = this.unlimitedUserTokens || this.tokens;
			if (!tokenList?.length) return 0;
			return tokenList
				.filter(token => (token.currency || 'USD').toUpperCase() === (this.selectedCurrency || 'USD').toUpperCase())
				.reduce((sum, token) => {
					const balance = token.balance || 0;
					const price = token.price?.buy || token.price?.exchange || 0;
					return sum + (balance * price);
				}, 0);
		},

		pendingOrdersCount() {
			return 0; // Will be populated from store
		},

		exchangeTokensCount() {
			return this.raisedTokens?.length || 0;
		}
	},

	watch: {
		organisationDetails(val) {
			if(!val.verified) {
				this.$router.push({ name: 'IdentityVerification' });
			}
		},
		walletBalance: {
			handler(newVal) {
				if (newVal && newVal.length > 0 && !this.selectedWallet) {
					this.selectedWallet = newVal[0];
					this.selectedCurrency = newVal[0].currency;
				}
			},
			immediate: true
		},
	},

	created() {
		this.getOverviewDetails();
		// Handle redirect with reason (e.g., from dealroom when user is already a member)
		this.handleRedirectReason();
	},

	mounted() {
		useFundNotification().checkFundingStatus();
	},

	methods: {
		holdingQuantity,
		// The deal type in words, the same one the list badges use.
		holdingType(token) {
			const label = listingView(token).badge.label;
			return label === "NGX Stock" ? "NGX stock" : label;
		},
		...mapActions([
			"showToast",
			"setLoading",
			,
			,
			,
			"getCarousel",
			,
		]),
		...mapActions("organisationModule", ["getWalletBalance", "getOrganisationProfile"]),
		...mapActions("TokenModule", ["getUserTokens", "getUnlimitedUserTokens"]),

		handleRedirectReason() {
			const { redirect, reason } = this.$route.query;
			if (redirect === 'true' && reason) {
				// Show toast with the reason message
				this.showToast({
					description: decodeURIComponent(reason),
					display: true,
					type: "info",
				});
				// Clean up query params without reloading
				this.$router.replace({ name: 'OverviewDetails' });
			}
		},

		formatAmountToDollar,
		getCurrencySymbol,

		formatNumber(value) {
			if (value === null || value === undefined) return '0';
			return new Intl.NumberFormat('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}).format(value);
		},

		selectCurrency(wallet) {
			this.selectedWallet = wallet;
			this.selectedCurrency = wallet.currency;
			this.$store.dispatch('organisationModule/setCurrency', wallet.currency);
		},

		goToToken(token) {
			this.$router.push({
				name: 'TokenOverview',
				params: { symbol: token.symbol, id: token._id }
			});
		},

		async getOverviewDetails() {
			this.loading = true;
			let query = { page: 1, limit: 10 };
			let unlimitedQuery = { page: 1, limit: 100000000000 };
			let userTokens = this.getUserTokens(query);
			let unlimitedTokens = this.getUnlimitedUserTokens(unlimitedQuery);
			let carousel = this.getCarousel();
			let userInfo = this.getOrganisationProfile();
			const walletBalance = this.getWalletBalance();
			try {
				await Promise.all([userTokens, unlimitedTokens, carousel, userInfo, walletBalance]);
				this.loading = false;
			} catch (error) {
				responseHandler.handleError(error);
			}
		},



	},
};
</script>

<style lang="scss" scoped>
// ═══════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS - LIGHT THEME
// ═══════════════════════════════════════════════════════════════════════════
$bg-primary: #F8FAFC;
$bg-secondary: #FFFFFF;
$bg-card: #FFFFFF;
$bg-card-hover: #F1F5F9;
$border-color: #E2E8F0;
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #94A3B8;
$accent-primary: #4D84FF;
$accent-solid: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$success: #10B981;
$error: #EF4444;
$warning: #F59E0B;

// ═══════════════════════════════════════════════════════════════════════════
// BASE STYLES
// ═══════════════════════════════════════════════════════════════════════════
.dashboard {
	background: $bg-primary;
	min-height: 100vh;
	padding-bottom: 40px;
	color: $text-primary;
}

// ═══════════════════════════════════════════════════════════════════════════
// HERO SECTION
// ═══════════════════════════════════════════════════════════════════════════
.hero-section {
	margin-bottom: 20px;
}

.hero-card {
	background: linear-gradient(135deg, #001240 0%, #0a2463 50%, #001240 100%);
	border-radius: 7px;
	padding: 24px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	position: relative;
	overflow: hidden;

	// Subtle pattern overlay
	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: 
			radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
			radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.02) 0%, transparent 50%);
		pointer-events: none;
	}
}

.hero-content {
	display: grid;
	grid-template-columns: 1fr;
	gap: 24px;
	position: relative;
	z-index: 1;

	@media (min-width: 1024px) {
		grid-template-columns: 2fr 1fr;
	}
}

.portfolio-main {
	display: flex;
	flex-direction: column;
}

.portfolio-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
	flex-wrap: wrap;
	gap: 12px;
}

.portfolio-label {
	color: rgba(255, 255, 255, 0.6);
	font-size: 14px;
	font-weight: 400;
}

.currency-toggle {
	display: flex;
	gap: 8px;
	background: rgba(255, 255, 255, 0.08);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	padding: 4px;
	border-radius: 4px;
	border: 1px solid rgba(255, 255, 255, 0.1);
}

.currency-btn {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 12px;
	border-radius: 4px;
	border: none;
	background: transparent;
	color: rgba(255, 255, 255, 0.7);
	font-size: 13px;
	font-weight: 400;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.1);
		color: #FFFFFF;
	}

	&.active {
		background: rgba(255, 255, 255, 0.18);
		backdrop-filter: blur(12px);
		-webkit-backdrop-filter: blur(12px);
		color: white;
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
	}
}

.portfolio-value-container {
	margin-bottom: 24px;
}

.portfolio-value {
	font-size: 48px;
	font-weight: 400;
	letter-spacing: -0.02em;
	margin-bottom: 8px;
	color: #FFFFFF;
	text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

	@media (max-width: 640px) {
		font-size: 36px;
	}
}

.positions-breakdown {
	margin: 4px 0 0;
	font-size: 14px;
	color: rgba(255, 255, 255, 0.7);
}

.shares-label {
	font-size: 24px;
	font-weight: 400;
	color: rgba(255, 255, 255, 0.6);

	@media (max-width: 640px) {
		font-size: 18px;
	}
}

.cash-value-display {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 16px;
}

.cash-value-label {
	color: rgba(255, 255, 255, 0.5);
	font-weight: 400;
}

.cash-value-amount {
	color: #FFFFFF;
	font-weight: 400;
}

// Monthly Stats
.monthly-stats {
	display: flex;
	gap: 24px;
	margin-top: 16px;
	padding-top: 16px;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.monthly-stat {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.monthly-stat-label {
	font-size: 12px;
	color: rgba(255, 255, 255, 0.5);
	font-weight: 400;
}

.monthly-stat-value {
	font-size: 16px;
	font-weight: 400;
	color: #FFFFFF;

	&.positive {
		color: #4ADE80;
	}

	&.negative {
		color: #F87171;
	}

	&.payout {
		color: #60A5FA;
	}
}

.period-btn {
	padding: 6px 12px;
	border-radius: 4px;
	border: none;
	background: transparent;
	color: rgba(255, 255, 255, 0.5);
	font-size: 12px;
	font-weight: 400;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		color: #FFFFFF;
		background: rgba(255, 255, 255, 0.1);
	}

	&.active {
		background: rgba(255, 255, 255, 0.15);
		color: #FFFFFF;
	}
}

// Quick Stats
.quick-stats {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.stat-item {
	// Glassmorphism effect
	background: rgba(255, 255, 255, 0.08);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 4px;
	padding: 14px;
	display: flex;
	flex-direction: column;
}

.stat-label {
	color: rgba(255, 255, 255, 0.6);
	font-size: 13px;
	margin-bottom: 8px;
}

.stat-value {
	font-size: 24px;
	font-weight: 400;
	color: #FFFFFF;
	margin-bottom: 8px;
}

.stat-meta {
	color: rgba(255, 255, 255, 0.7);
	font-size: 13px;
}

.stat-link {
	color: #60A5FA;
	font-size: 13px;
	font-weight: 400;
	text-decoration: none;
	transition: color 0.2s ease;

	&:hover {
		color: #93C5FD;
	}
}

.fund-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	padding: 12px;
	background: rgba(255, 255, 255, 0.15);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 4px;
	color: white;
	font-size: 14px;
	font-weight: 400;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.25);
		border-color: rgba(255, 255, 255, 0.3);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
	}
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN GRID
// ═══════════════════════════════════════════════════════════════════════════
.main-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 16px;

	@media (min-width: 1200px) {
		grid-template-columns: 1fr 340px;
	}
}

.main-column {
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-width: 0;
}

.side-column {
	display: flex;
	flex-direction: column;
	gap: 14px;
	width: 100%;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTENT SECTIONS
// ═══════════════════════════════════════════════════════════════════════════
.content-section {
	position: relative;
}

.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	flex-wrap: wrap;
	gap: 10px;
}

.section-title-group {
	display: flex;
	align-items: center;
	gap: 12px;
}

.section-title {
	font-size: 17px;
	font-weight: 400;
	color: $text-primary;
}

.live-indicator {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 4px 10px;
	background: rgba(16, 185, 129, 0.15);
	border-radius: 7px;
	font-size: 12px;
	font-weight: 400;
	color: $success;
}

.live-dot {
	width: 6px;
	height: 6px;
	background: $success;
	border-radius: 50%;
	animation: pulse 2s infinite;
}

@keyframes pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.5; }
}

.badge-count {
	padding: 4px 10px;
	background: rgba(77, 132, 255, 0.15);
	border-radius: 7px;
	font-size: 12px;
	font-weight: 400;
	color: $accent-primary;
}

.section-link {
	color: $accent-primary;
	font-size: 14px;
	font-weight: 400;
	text-decoration: none;
	transition: color 0.2s ease;

	&:hover {
		color: lighten($accent-primary, 15%);
	}
}

.opportunities-grid {
	background: transparent;
	border: none;
	border-radius: 0;
	padding: 0;
	overflow: visible;
}

.activity-card {
	background: $bg-card;
	border: 1px solid $border-color;
	border-radius: 10px;
	padding: 20px;
	overflow: hidden;
}

.orders-card {
	background: transparent;
	border: none;
	border-radius: 0;
	padding: 0;
	margin-top: 8px;
}

// ═══════════════════════════════════════════════════════════════════════════
// SIDE CARDS
// ═══════════════════════════════════════════════════════════════════════════
.side-card {
	background: $bg-card;
	border: 1px solid $border-color;
	border-radius: 10px;
	padding: 16px;
	width: 100%;
	box-sizing: border-box;
}

.side-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.side-title {
	font-size: 15px;
	font-weight: 400;
	color: $text-primary;
}

.side-link {
	color: $accent-primary;
	font-size: 13px;
	font-weight: 400;
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
}

// Holdings
.holdings-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.holding-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px;
	background: #F8FAFC;
	border-radius: 4px;
	cursor: pointer;
	transition: background 0.2s ease;

	&:hover {
		background: #F1F5F9;
	}
}

.holding-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.holding-logo {
	width: 40px;
	height: 40px;
	border-radius: 7px;
	object-fit: cover;
}

.holding-name {
	font-weight: 400;
	font-size: 14px;
	color: $text-primary;
	margin-bottom: 2px;
}

.holding-symbol {
	font-size: 12px;
	color: $text-muted;
}

.holding-value {
	text-align: right;
}

.holding-amount {
	font-weight: 400;
	font-size: 14px;
	color: $text-primary;
	margin-bottom: 2px;
}

.holding-shares {
	font-size: 12px;
	color: $text-muted;
}

// Empty State
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 24px;
	text-align: center;
}

.empty-icon {
	width: 64px;
	height: 64px;
	color: $text-muted;
	margin-bottom: 16px;

	svg {
		width: 100%;
		height: 100%;
	}
}

.empty-text {
	color: $text-secondary;
	font-size: 14px;
	margin-bottom: 16px;
}

.empty-cta {
	padding: 10px 20px;
	background: $btn-primary;
	border-radius: 7px;
	color: white;
	font-size: 14px;
	font-weight: 400;
	text-decoration: none;
	transition: all 0.2s ease;

	&:hover {
		background: $btn-primary-hover;
		box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
	}
}

// ═══════════════════════════════════════════════════════════════════════════
// WELCOME MODAL
// ═══════════════════════════════════════════════════════════════════════════
.welcome-modal {
	text-align: center;
	padding: 16px;
}

.welcome-icon {
	width: 80px;
	height: 80px;
	margin: 0 auto 24px;
	color: $accent-primary;

	svg {
		width: 100%;
		height: 100%;
	}
}

.welcome-title {
	font-size: 24px;
	font-weight: 700;
	color: $text-primary;
	margin-bottom: 8px;
}

.welcome-text {
	color: $text-secondary;
	font-size: 15px;
}

</style>
